import { useState, useEffect, useMemo } from "react";
import { fetcher } from "../../../../services/fetcher";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { errorNotifications, successNotifications } from "../../../../utils/handleNotifications";
import dayjs from "dayjs";
import WizardShell from "./wizard/WizardShell";
import { useWizardSteps } from "./hooks/useWizardSteps";
import { USER_TYPES } from "../../../../utils/userTypes";

const endpoint = "issuance/create-policy/";

const SingleIssuance = () => {
  const [issueAs, setIssueAs] = useState("User");
  const [selectedOperator, setSelectedOperator] = useState(null);
  const [operatorMeta, setOperatorMeta] = useState({ name: "" });
  const [quoteData, setQuoteData] = useState(null);
  const [coverOption, setCoverOption] = useState("");
  const [showQuoteLink, setShowQuoteLink] = useState(false);
  const [showTravelerForm, setShowTravelerForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [originalAmount, setOriginalAmount] = useState(null);
  const [discountedAmount, setDiscountedAmount] = useState(null);
  const [isCouponApplying, setIsCouponApplying] = useState(false);
  const [isCouponApplied, setIsCouponApplied] = useState(false);
  const [advanceAfterQuote, setAdvanceAfterQuote] = useState(false);
  const [tripPreview, setTripPreview] = useState(null);

  const role = useSelector((state) => state.auth.role);
  const user_type = useSelector((state) => state.auth.user_type);

  const navigate = useNavigate();

  const userId = useSelector((state) => state.userProfile.id);
  const applyCoupon = async () => {
    if (!couponCode) {
      errorNotifications("Please enter a valid coupon code.");
      return;
    }
    setIsCouponApplying(true);
    try {
      const response = await fetcher("issuance/apply-coupon/", "POST", {
        user_id: userId,
        coupon_code: couponCode,
        original_amount: quoteData.total_price[coverOption], // Pass selected cover's amount
      });

      setOriginalAmount(response.original_amount);
      setDiscountedAmount(response.discounted_amount);
      successNotifications(`Coupon applied! Discounted Amount: ${response.discounted_amount}`);
      setIsCouponApplied(true);
    } catch (error) {
      errorNotifications(error.response.data.message || "Failed to apply coupon.");
    } finally {
      setIsCouponApplying(false);
    }
  };

  const handleQuoteReady = (_url, data) => {
    setQuoteData(data ?? null);
    setCoverOption("");
    setOriginalAmount(null);
    setDiscountedAmount(null);
    setIsCouponApplied(false);
    setAdvanceAfterQuote(true);
  };

  const getFilteredCovers = (covers, totalPrice, currency) => {
    return Object.keys(covers)
      .filter((coverType) => covers[coverType].length > 0)
      .map((coverType) => ({
        type: coverType,
        price: totalPrice[coverType],
        currency: currency,
      }));
  };

  const filteredCovers = useMemo(() => {
    if (!quoteData) return [];
    return getFilteredCovers(quoteData.covers, quoteData.total_price, quoteData.currency);
  }, [quoteData]);

  const issuePolicyMutation = async (policy) => {
    setIsSubmitting(true);
    const createPolicy = selectedOperator
      ? `${endpoint}?operator=${selectedOperator}`
      : endpoint;

    try {
      await fetcher(createPolicy, "POST", policy, {
        "content-type": "multipart/form-data",
      });
      const path =
        role.includes("Operator") || user_type.includes(USER_TYPES.OPERATOR)
          ? "/operator/operator-details/policies-issued"
          : user_type.includes(USER_TYPES.SUPERUSER) ||
              role.some((r) =>
                ["Super Admin", "Issuance Team", "Sales Team"].includes(r)
              )
            ? "/issuance/all-policy"
            : user_type.includes(USER_TYPES.STAFF)
              ? "/issuance/user-specific-policy"
              : "/user/user-details/policies-issued";
      navigate(path);
      successNotifications("Success! Policy Issue");
    } catch (error) {
      errorNotifications(error.response.data, error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTraveler = (
    allTravelersData,
    operator_id = null,
    operator_name = null,
    operator_phone = null
  ) => {
    const formData = new FormData();

    const travellers = allTravelersData.map((traveler) => {
      return Object.entries({
        cover_pricing_id: traveler.cover_pricing_id,
        indian_resident: traveler.indian_resident,
        first_name: traveler.first_name,
        last_name: traveler.last_name,
        email: traveler.email,
        gender: traveler.gender,
        nationality: traveler.nationality,
        phone_number: traveler.phone_number,
        dob: traveler.dob ? dayjs(traveler.dob).format("YYYY-MM-DD") : "",
        is_primary: traveler.is_primary || false,
        nominee_name: traveler.nominee_name,
        nominee_relation: traveler.nominee_relation,
        passport_number: traveler.passport_number || "",
        emergency_email: traveler.emergency_email || "",
        emergency_name: traveler.emergency_name || "",
        emergency_contact: traveler.emergency_contact || "",
        passport_back: traveler.passport_back || null,
        passport_front: traveler.passport_front || null,
        aadhar_card_front: traveler.aadhar_card_front || null,
        aadhar_card_back: traveler.aadhar_card_back || null,
        flight_ticket: traveler.flight_ticket || null,
        residence_proof: traveler.residence_proof || null,
      }).reduce((acc, [key, value]) => {
        if (value !== null && value !== "") {
          acc[key] = value;
        }
        return acc;
      }, {});
    });

    const payload = {
      traveller_from: quoteData.quote_data.travelling_from || "",
      traveller_to: quoteData.quote_data.travelling_to || "",
      coverage_start_date: quoteData.quote_data.start_date || "",
      coverage_end_date: quoteData.quote_data.end_date || "",
      no_of_pax: quoteData.quote_data.age_fields.length || "",
      tour_operator_name: operator_name || "",
      operator_phone_number: operator_phone && operator_phone,
      operator_profile_id: selectedOperator || operator_id,
      operator_profile_name: operator_name || "",
      travellers: travellers,
      ...(couponCode && couponCode.trim() !== "" && { coupon_code: couponCode.trim() }),
    };

    // Append payload data to FormData
    for (const key in payload) {
      if (Object.prototype.hasOwnProperty.call(payload, key)) {
        if (key === "travellers") {
          payload[key].forEach((traveler, idx) => {
            for (const field in traveler) {
              if (Object.prototype.hasOwnProperty.call(traveler, field)) {
                formData.append(`travellers[${idx}].${field}`, traveler[field]);
              }
            }
          });
        } else {
          formData.append(key, payload[key]);
        }
      }
    }

    issuePolicyMutation(formData);
  };

  const handleContinue = () => {
    setShowQuoteLink(false);
    setShowTravelerForm(true);
  };

  const handleCoverChange = (value) => {
    setCoverOption(value);
    setShowQuoteLink(false);
  };

  useEffect(() => {
    if (filteredCovers.length === 1) {
      setCoverOption(filteredCovers[0].type);
    }
  }, [filteredCovers]);

  const wizard = useWizardSteps({ quoteData, coverOption });
  const { activeStep, nextStep } = wizard;

  useEffect(() => {
    if (advanceAfterQuote && quoteData && activeStep === 0) {
      nextStep();
      setAdvanceAfterQuote(false);
    }
  }, [advanceAfterQuote, quoteData, activeStep, nextStep]);

  const finalAmount =
    originalAmount !== null && discountedAmount !== null
      ? originalAmount - discountedAmount
      : undefined;

  return (
    <WizardShell
      wizard={wizard}
      issueAs={issueAs}
      onIssueAsChange={(e) => {
        setIssueAs(e.target.value);
        if (e.target.value !== "Operator") {
          setSelectedOperator(null);
          setOperatorMeta({ name: "" });
        }
      }}
      selectedOperator={selectedOperator}
      setSelectedOperator={setSelectedOperator}
      operatorMeta={operatorMeta}
      onOperatorMeta={setOperatorMeta}
      onUrlReady={handleQuoteReady}
      tripPreview={tripPreview}
      onTripPreview={setTripPreview}
      quoteData={quoteData}
      filteredCovers={filteredCovers}
      coverOption={coverOption}
      onCoverChange={handleCoverChange}
      couponCode={couponCode}
      setCouponCode={setCouponCode}
      applyCoupon={applyCoupon}
      isCouponApplying={isCouponApplying}
      isCouponApplied={isCouponApplied}
      originalAmount={originalAmount}
      discountedAmount={discountedAmount}
      finalAmount={finalAmount}
      onContinue={handleContinue}
      showTravelerForm={showTravelerForm}
      showQuoteLink={showQuoteLink}
      openQuoteLink={() => setShowQuoteLink(true)}
      closeQuoteLink={() => setShowQuoteLink(false)}
      isSubmitting={isSubmitting}
      handleTraveler={handleTraveler}
    />
  );
};

export default SingleIssuance;
